import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pencil,
  Search,
  Trash2,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import {
  useBulkPublishBackfilled,
  useCoachesList,
  useDeleteCoach,
  type AdminCoach,
} from "@/lib/queries/coaches"
import { ExportButton } from "@/components/ExportButton"
import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// PAGE: Coaches (list) — V-D
// API:    POST /admin/coach_list    (server pagination/sort/search)
//         POST /admin/coach_delete  (delete)
//
// Shows ALL coaches — published + unpublished — so admins can promote
// backfilled records after editing them.
// ───────────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const

function useDebounced<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return v
}

export default function Coaches() {
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounced(search, 300)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(10)
  const [sortyBy, setSortyBy] = useState<string | undefined>(undefined)
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1)

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, limit, sortyBy, sortDirection])

  const params = useMemo(
    () => ({
      page,
      limit,
      search: debouncedSearch || undefined,
      sortyBy,
      sortDirection,
      // V-Q11 (Kush 2026-08-25): keep the ~33 backfilled author-stub
      // coach docs (JIM ROHN, TONY ROBBINS, etc. — is_published:false,
      // exist only to resolve narrator_coach_id FKs on legacy catalog
      // content) OUT of the ops Creators directory. Real coaches all
      // have is_published:true.
      is_published: true,
    }),
    [page, limit, debouncedSearch, sortyBy, sortDirection]
  )

  const coachesQ = useCoachesList(params)
  const deleteMut = useDeleteCoach()
  const bulkPublishMut = useBulkPublishBackfilled()
  const [bulkPublishOpen, setBulkPublishOpen] = useState(false)
  const [bulkPublishResult, setBulkPublishResult] = useState<string | null>(
    null
  )

  const [deleteCoach, setDeleteCoach] = useState<AdminCoach | null>(null)

  const onSortClick = (field: string) => {
    if (sortyBy === field) {
      setSortDirection((prev) => (prev === 1 ? -1 : 1))
    } else {
      setSortyBy(field)
      setSortDirection(-1)
    }
  }

  const SortIcon = ({ field }: { field: string }) => {
    if (sortyBy !== field) return null
    return sortDirection === 1 ? (
      <ArrowUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3" />
    )
  }

  const totalPages = coachesQ.data?.totalPages ?? 0
  const totalDocs = coachesQ.data?.totalDocs ?? 0

  const handleDeleteConfirm = async () => {
    if (!deleteCoach) return
    await deleteMut.mutateAsync(deleteCoach._id)
    setDeleteCoach(null)
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="gap-4 sm:flex-row sm:items-end sm:justify-between sm:space-y-0">
          <div>
            <CardTitle>Creators</CardTitle>
            <CardDescription>
              {totalDocs > 0
                ? `${totalDocs.toLocaleString()} total`
                : "Narrators promoted to first-class records"}
            </CardDescription>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <div className="relative w-full sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search creators…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                aria-label="Search creators"
              />
            </div>
            <ExportButton<AdminCoach>
              filenamePrefix="coaches"
              totalEstimate={totalDocs}
              columns={[
                { label: "Name", get: (c) => c.name },
                { label: "Slug", get: (c) => c.slug ?? "" },
                {
                  label: "Topics",
                  get: (c) => (Array.isArray(c.topics) ? c.topics.join("; ") : ""),
                },
                { label: "Source", get: (c) => c.source ?? "" },
                {
                  label: "Published",
                  get: (c) => (c.is_published ? "yes" : "no"),
                },
                {
                  label: "Order",
                  get: (c) =>
                    typeof c.display_order === "number" ? c.display_order : 0,
                },
                { label: "ID", get: (c) => c._id },
              ]}
              request={async (page, limit) => {
                const res = await api.post("/admin/coach_list", {
                  page,
                  limit,
                  search: debouncedSearch || undefined,
                  sortyBy,
                  sortDirection,
                })
                return {
                  docs: res.data?.data?.docs ?? [],
                  totalDocs: res.data?.data?.totalDocs ?? 0,
                }
              }}
            />
            <Button
              variant="outline"
              onClick={() => setBulkPublishOpen(true)}
              disabled={bulkPublishMut.isPending}
            >
              Publish all backfilled
            </Button>
            <Button asChild>
              <Link to="/coaches/new">Add creator</Link>
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Bulk-publish confirm */}
      <Dialog
        open={bulkPublishOpen}
        onOpenChange={(o) => {
          if (!o) {
            setBulkPublishOpen(false)
            setBulkPublishResult(null)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish all backfilled creators?</DialogTitle>
            <DialogDescription>
              This flips <code>is_published = true</code> on every coach
              record whose <code>source = "backfilled"</code>. They will
              start appearing in the mobile directory + guest feed
              immediately. Idempotent — already-published rows are skipped.
            </DialogDescription>
          </DialogHeader>
          {bulkPublishResult && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
              {bulkPublishResult}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setBulkPublishOpen(false)
                setBulkPublishResult(null)
              }}
            >
              {bulkPublishResult ? "Close" : "Cancel"}
            </Button>
            {!bulkPublishResult && (
              <Button
                disabled={bulkPublishMut.isPending}
                onClick={async () => {
                  try {
                    const res = await bulkPublishMut.mutateAsync()
                    const data = res?.data || { matched: 0, modified: 0 }
                    setBulkPublishResult(
                      `Published ${data.modified} of ${data.matched} backfilled creators.`
                    )
                  } catch (e) {
                    setBulkPublishResult(
                      e instanceof Error ? e.message : "Bulk publish failed."
                    )
                  }
                }}
              >
                {bulkPublishMut.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Publish them all
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="p-0">
          {coachesQ.isError && (
            <div
              role="alert"
              className="m-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Failed to load coaches. Try refreshing.</span>
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">Avatar</TableHead>
                <SortableTH field="name" label="Name" {...{ onSortClick, SortIcon }} />
                <TableHead>Topics</TableHead>
                <TableHead className="w-28">Source</TableHead>
                <TableHead className="w-28">Published</TableHead>
                <SortableTH
                  field="display_order"
                  label="Order"
                  className="w-20"
                  {...{ onSortClick, SortIcon }}
                />
                <SortableTH
                  field="created_at"
                  label="Created"
                  className="w-32"
                  {...{ onSortClick, SortIcon }}
                />
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {coachesQ.isLoading && (
                <TableRow>
                  <TableCell colSpan={8} className="h-32 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </TableCell>
                </TableRow>
              )}
              {!coachesQ.isLoading && (coachesQ.data?.docs.length ?? 0) === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="h-32 text-center text-muted-foreground">
                    No coaches match your filters.
                  </TableCell>
                </TableRow>
              )}
              {coachesQ.data?.docs.map((c) => (
                <TableRow key={c._id}>
                  <TableCell>
                    {c.avatar_url ? (
                      <img
                        src={c.avatar_url}
                        alt=""
                        loading="lazy"
                        className="h-12 w-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="grid h-12 w-12 place-items-center rounded-full bg-muted text-xs text-muted-foreground">
                        —
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-medium">
                    {c.name || "—"}
                    {c.slug && (
                      <div className="text-xs font-normal text-muted-foreground">
                        {c.slug}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {Array.isArray(c.topics) && c.topics.length > 0
                      ? c.topics.join(", ")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        "inline-flex items-center rounded-full px-2 py-0.5 text-xs",
                        c.source === "backfilled"
                          ? "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200"
                          : "bg-secondary text-secondary-foreground"
                      )}
                    >
                      {c.source ?? "admin"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        "inline-flex items-center rounded-full px-2 py-0.5 text-xs",
                        c.is_published
                          ? "bg-green-100 text-green-900 dark:bg-green-900/40 dark:text-green-200"
                          : "bg-muted text-muted-foreground"
                      )}
                    >
                      {c.is_published ? "published" : "draft"}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {typeof c.display_order === "number" ? c.display_order : "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {c.created_at
                      ? new Date(c.created_at).toLocaleDateString()
                      : "—"}
                  </TableCell>
                  <TableCell className="space-x-1 text-right">
                    <Button asChild variant="ghost" size="sm" aria-label="Edit creator">
                      <Link to={`/coaches/${c._id}`}>
                        <Pencil className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteCoach(c)}
                      aria-label="Delete creator"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>

        <div className="flex flex-col gap-3 border-t px-4 py-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              aria-label="Rows per page"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span>
              Page {page}
              {totalPages > 0 ? ` of ${totalPages}` : ""}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={totalPages > 0 && page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Dialog
        open={!!deleteCoach}
        onOpenChange={(open) => !open && setDeleteCoach(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete creator?</DialogTitle>
            <DialogDescription>
              {deleteCoach?.name || "This creator"} will be removed. Followers
              and content references will remain but the creator record will no
              longer appear in directories. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteCoach(null)}
              disabled={deleteMut.isPending}
            >
              Keep creator
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete creator
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function SortableTH({
  field,
  label,
  className,
  onSortClick,
  SortIcon,
}: {
  field: string
  label: React.ReactNode
  className?: string
  onSortClick: (field: string) => void
  SortIcon: React.ComponentType<{ field: string }>
}) {
  return (
    <TableHead className={cn("cursor-pointer select-none", className)}>
      <button
        type="button"
        onClick={() => onSortClick(field)}
        className="flex items-center gap-1 text-left font-medium text-muted-foreground hover:text-foreground"
      >
        {label}
        <SortIcon field={field} />
      </button>
    </TableHead>
  )
}
